import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "./client";
import type { Me, User, UserSession, Workspace } from "./types";

export const qk = {
  me: ["me"] as const,
  workspaces: ["workspaces"] as const,
  sessions: ["sessions"] as const,
};

/** Usuário da sessão; `null` = sem sessão (401). */
export function useMe() {
  return useQuery({
    queryKey: qk.me,
    queryFn: async (): Promise<Me | null> => {
      try {
        const res = await api.get<{
          user: User;
          isPlatformAdmin?: boolean;
          assistant?: { enabled: boolean };
          attachments?: { enabled: boolean };
        }>("/auth/me");
        return {
          ...res.user,
          isPlatformAdmin: res.isPlatformAdmin ?? false,
          assistantEnabled: res.assistant?.enabled ?? false,
          attachmentsEnabled: res.attachments?.enabled ?? false,
        };
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 60_000,
  });
}

export function useWorkspaces() {
  return useQuery({
    queryKey: qk.workspaces,
    queryFn: async () => (await api.get<{ workspaces: Workspace[] }>("/workspaces")).workspaces,
  });
}

export function useRequestCode() {
  return useMutation({ mutationFn: (email: string) => api.post("/auth/login-code", { email }) });
}

export function useVerifyCode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; code: string }) => api.post<{ user: User }>("/auth/verify", input),
    onSuccess: ({ user }) => {
      qc.clear();
      // O login devolve só o usuário; o /me completa se ele administra a plataforma.
      qc.setQueryData<Me>(qk.me, {
        ...user,
        isPlatformAdmin: false,
        assistantEnabled: false,
        attachmentsEnabled: false,
      });
      void qc.invalidateQueries({ queryKey: qk.me });
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/auth/logout"),
    onSettled: () => {
      qc.clear();
      qc.setQueryData(qk.me, null);
    },
  });
}

/** Sessões ativas do usuário (todas as do usuário, não só deste workspace). */
export function useSessions() {
  return useQuery({
    queryKey: qk.sessions,
    queryFn: async () => (await api.get<{ sessions: UserSession[] }>("/auth/sessions")).sessions,
  });
}

/** Desconecta outra sessão (a atual sai pelo `useLogout`). */
export function useRevokeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/auth/sessions/${encodeURIComponent(id)}`),
    onSuccess: (_, id) => {
      qc.setQueryData<UserSession[]>(qk.sessions, (list) => list?.filter((s) => s.id !== id));
      void qc.invalidateQueries({ queryKey: qk.sessions });
    },
  });
}
